// src/components/AppraisalModal.jsx
// Professional salary appraisal modal.
// Gross salary components (Basic/HRA/LTA/Medical/Telephone/Special) are FIXED
// values — the calculator only suggests a starting point from a target gross,
// admin edits and saves them as fixed. Variable Pay / Retention / Joining
// Bonus are always manually filled. Total CTC = fixed annual gross + those.
// Extras: KPI setting for variable-pay employees, enriched appraisalLog entries.

import React, { useState, useMemo } from "react";
import { db } from "../firebase";
import { doc, updateDoc, arrayUnion, serverTimestamp } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { computeGrossBreakdown, sumGrossBreakdown, scaleGrossBreakdown, splitRetentionTranches, computeTotalCTC, fmtINR, r2, parseCTCNumber } from "../utils/salaryUtils";
import { FaTimes, FaTrophy, FaArrowRight, FaArrowUp, FaArrowDown, FaMagic, FaUserTie } from "react-icons/fa";
import toast from "react-hot-toast";
import styles from "./AppraisalModal.module.css";

/**
 * AppraisalModal
 * @param {object}   employee         — record with .employeeId, .name, .salaryBreakdown, .variableComponents, .uid/.id
 * @param {function} onClose          — close callback
 * @param {function} onAppraisalDone  — called with new appraisal entry after save
 * @param {function} onPromoteDone    — called with new role entry after promote save
 */
const AppraisalModal = ({ employee, onClose, onAppraisalDone, onPromoteDone }) => {
  const { userData } = useAuth();
  const isSuperAdmin = userData?.role === "superadmin";

  // ── Current fixed breakdown + variable components (read-only reference) ──
  const currentBreakdown = employee?.salaryBreakdown && Number(employee.salaryBreakdown.basic) > 0
    ? employee.salaryBreakdown : null;
  const hasFixedBreakdown = !!currentBreakdown;
  const currentVariable  = employee?.variableComponents || {};
  const fallbackGross = parseCTCNumber(String(employee?.grossMonthlySalary || 0)) || 0;
  // If stored fixed breakdown is absent, compute a suggested breakdown from gross so UI shows meaningful values
  const suggestedBreakdown = !hasFixedBreakdown && fallbackGross > 0 ? computeGrossBreakdown(fallbackGross) : null;
  const displayCurrentBreakdown = hasFixedBreakdown ? currentBreakdown : (suggestedBreakdown || { basic:0,hra:0,lta:0,medical:0,telephone:0,special:0 });
  const { monthlyGross: displayCurrentMonthlyGross, annualGross: displayCurrentAnnualGross } = sumGrossBreakdown(displayCurrentBreakdown);
  const currentMonthlyGross = displayCurrentMonthlyGross || fallbackGross;
  const currentAnnualGross = displayCurrentAnnualGross || (currentMonthlyGross * 12);
  const currentTotalCTC     = computeTotalCTC(displayCurrentBreakdown, currentVariable);

  // ── Active tab: "salary" | "promote" ────────────────────────────────────
  const [activeTab, setActiveTab] = useState("salary");

  // ── Promote tab state ────────────────────────────────────────────────────
  const [promoteRole,     setPromoteRole]     = useState(employee?.role || "employee");
  const [promotePosition, setPromotePosition] = useState(employee?.position || "");
  const [promoteDept,     setPromoteDept]     = useState(employee?.department || "");
  const [promoteDate,     setPromoteDate]     = useState(new Date().toISOString().split("T")[0]);
  const [promoteReason,   setPromoteReason]   = useState("");
  const [promoteSaving,   setPromoteSaving]   = useState(false);

  // ── Common fields ──────────────────────────────────────────────────────
  const [effectiveDate, setEffectiveDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  // Whether to include variable/retention changes in this appraisal
  // (retain as manual toggle, but we will not use percent-based inputs)
  const [includeVarRet, setIncludeVarRet] = useState(true);

  // ── Part 1: Gross salary ─────────────────────────────────────────────────
  // First-time setup (no fixed breakdown yet): calculator suggests, admin edits
  // freely before it becomes fixed. Once fixed, appraisals only take a new
  // target gross and the existing component ratios are scaled proportionally
  // — Basic/HRA/LTA/Medical/Telephone/Special are never hand-edited again.
  const [basicInput,      setBasicInput]      = useState(String(currentBreakdown?.basic || ""));
  const [hraInput,        setHraInput]        = useState(String(currentBreakdown?.hra || ""));
  const [ltaInput,        setLtaInput]        = useState(String(currentBreakdown?.lta || ""));
  const [medicalInput,    setMedicalInput]    = useState(String(currentBreakdown?.medical || ""));
  const [telephoneInput,  setTelephoneInput]  = useState(String(currentBreakdown?.telephone || ""));
  const [specialInput,    setSpecialInput]    = useState(String(currentBreakdown?.special || ""));
  const [hikePctInput,    setHikePctInput]    = useState("");

  // Note: gross calculator and manual component editing removed for a simpler UI.

  // Appraisal hike % applies on top of the current saved monthly gross; ratios
  // of the existing fixed breakdown are preserved via scaleGrossBreakdown.
  const hikePct = parseFloat(hikePctInput) || 0;
  const targetGrossFromHike = currentMonthlyGross * (1 + hikePct / 100);

  const scaledBreakdown = useMemo(() => {
    // For fixed breakdowns, only scale when a real hike percent is entered
    if (hasFixedBreakdown && hikePct !== 0) return scaleGrossBreakdown(currentBreakdown, targetGrossFromHike);
    // No fixed breakdown: only compute a suggested breakdown when a hike% is entered
    if (!hasFixedBreakdown && hikePct !== 0 && targetGrossFromHike > 0) {
      return computeGrossBreakdown(targetGrossFromHike) || null;
    }
    return null;
  }, [hasFixedBreakdown, currentBreakdown, targetGrossFromHike, hikePct]);

  const newBreakdown = useMemo(() => {
    if (hasFixedBreakdown) return scaledBreakdown || currentBreakdown;
    // If no fixed breakdown, only compute suggestion when a hike% is entered
    if (!hasFixedBreakdown && scaledBreakdown) return scaledBreakdown;
    // Otherwise return zeroed breakdown so preview remains empty unless changes provided
    return {
      basic: 0, hra: 0, lta: 0, medical: 0, telephone: 0, special: 0,
    };
  }, [hasFixedBreakdown, scaledBreakdown, currentBreakdown]);

  const { monthlyGross: newMonthlyGross, annualGross: newAnnualGross } = sumGrossBreakdown(newBreakdown);

  // ── Part 2: Variable & one-time components — superadmin-only edit ──────
  // Only Variable Pay Pool may be changed during appraisal. Joining bonus
  // and retention pools are one-time / multi-year commitments and are NOT
  // editable via appraisal — they remain as recorded in `employee_records_complete`.
  const [variablePayPoolInput,  setVariablePayPoolInput]  = useState(String(currentVariable.variablePayPool || ""));

  const newVariableComponents = useMemo(() => {
    // Joining and retention are immutable here; variable pool may be changed
    const currentVarPool = Number(currentVariable.variablePayPool) || 0;
    const computedVariablePool = includeVarRet
      ? (parseFloat(variablePayPoolInput) || currentVarPool || 0)
      : (currentVarPool || 0);

    return {
      joiningBonus:    Number(currentVariable.joiningBonus) || 0,
      variablePayPool: computedVariablePool,
      retentionPool:   Number(currentVariable.retentionPool) || 0,
    };
  }, [isSuperAdmin, variablePayPoolInput, currentVariable]);

  const retentionTranches = useMemo(
    () => splitRetentionTranches(newVariableComponents.retentionPool),
    [newVariableComponents.retentionPool]
  );

  const newTotalCTC = useMemo(
    () => computeTotalCTC(newBreakdown, newVariableComponents),
    [newBreakdown, newVariableComponents]
  );

  const hasChanges = hikePct !== 0 || (includeVarRet && ((parseFloat(variablePayPoolInput) || 0) !== 0));

  // Effective % change on Total CTC (shown as badge)
  const ctcHikePct = useMemo(() => {
    // Prefer the stored annual CTC on the employee record if available
    const empAnnual = parseCTCNumber(String(employee?.annualCTC || "")) || 0;
    const baseOldCtc = empAnnual > 0 ? empAnnual : (currentTotalCTC || 0);
    const newCtc = Number(newTotalCTC) || 0;
    if (!baseOldCtc || !newCtc) return 0;
    return parseFloat((((newCtc - baseOldCtc) / baseOldCtc) * 100).toFixed(2));
  }, [currentTotalCTC, newTotalCTC]);

  // ── KPI (only for variable-pay employees) ─────────────────────────────
  const [setKpi, setSetKpi]         = useState(false);
  const [kpiPctInput, setKpiPctInput] = useState("80");

  const kpiPct    = Math.min(100, Math.max(0, parseFloat(kpiPctInput) || 0));
  const kpiFloat  = kpiPct / 100;
  const showKpiSection = !!(newVariableComponents.variablePayPool > 0 || currentVariable.variablePayPool > 0);
  const estimatedVariableRelease =
    setKpi && newVariableComponents.variablePayPool > 0
      ? Math.round(newVariableComponents.variablePayPool * kpiFloat)
      : 0;

  // Allow save when effective date present and at least one of the percentages is non-zero
  const anyVarRetVal = (parseFloat(variablePayPoolInput) || 0);
  const canSave = Boolean(effectiveDate) && (
    hikePct !== 0 ||
    (includeVarRet && anyVarRetVal !== 0) ||
    (!hasFixedBreakdown && fallbackGross > 0 && hikePct !== 0)
  );

  // ── Promote Save ───────────────────────────────────────────────────────
  const handlePromote = async () => {
    if (!promoteDate) {
      toast.error("Select an effective date");
      return;
    }
    if (promoteRole === (employee?.role || "employee")) {
      toast.error("New role is the same as the current role");
      return;
    }
    setPromoteSaving(true);
    try {
      const empRecordId = employee.employeeId || employee.id;
      const roleEntry = {
        date:          promoteDate,
        oldRole:       employee.role || "employee",
        newRole:       promoteRole,
        oldPosition:   employee.position || "",
        newPosition:   promotePosition.trim(),
        oldDepartment: employee.department || "",
        newDepartment: promoteDept.trim(),
        note:          promoteReason.trim(),
        promotedBy:    userData?.name || userData?.email || "admin",
        promotedAt:    new Date().toISOString(),
      };
      const updateFields = {
        role:     promoteRole,
        roleLog:  arrayUnion(roleEntry),
        updatedAt: serverTimestamp(),
      };
      if (promotePosition.trim()) updateFields.position = promotePosition.trim();
      if (promoteDept.trim())     updateFields.department = promoteDept.trim();
      await updateDoc(doc(db, "employee_records_complete", empRecordId), updateFields);

      const userUid = employee.uid || (employee.id !== employee.employeeId ? employee.id : null);
      if (userUid) {
        try {
          const userUpdate = { role: promoteRole };
          if (promotePosition.trim()) userUpdate.position = promotePosition.trim();
          if (promoteDept.trim())     userUpdate.department = promoteDept.trim();
          await updateDoc(doc(db, "users", userUid), userUpdate);
        } catch (syncErr) {
          console.warn("Could not sync to users collection:", syncErr);
        }
      }

      toast.success(`Role changed to ${promoteRole}!`);
      if (onPromoteDone) onPromoteDone(roleEntry);
      onClose();
    } catch (e) {
      console.error("Promote save error:", e);
      toast.error("Failed to save role change: " + e.message);
    } finally {
      setPromoteSaving(false);
    }
  };

  // ── Save ───────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!canSave) {
      toast.error("Enter a valid gross salary breakdown and effective date");
      return;
    }
    setSaving(true);
    try {
      const empRecordId = employee.employeeId || employee.id;
      const newGross    = Math.round(newMonthlyGross || 0);

      const appraisalEntry = {
        date:             effectiveDate,
        oldCTC:           String(currentTotalCTC || 0),
        newCTC:           String(newTotalCTC),
        oldMonthlyGross:  String(Math.round(currentMonthlyGross || 0)),
        newMonthlyGross:  String(newGross),
        hikePct:          String(ctcHikePct),
        oldVariablePool:  String(currentVariable.variablePayPool || 0),
        newVariablePool:  String(newVariableComponents.variablePayPool || 0),
        oldRetentionPool: String(currentVariable.retentionPool || 0),
        newRetentionPool: String(newVariableComponents.retentionPool || 0),
        kpiSet:           setKpi ? String(kpiFloat) : null,
        variableReleased: setKpi && newVariableComponents.variablePayPool > 0
          ? String(estimatedVariableRelease)
          : null,
        note:             reason.trim(),
        revisedBy:        userData?.name || userData?.email || "admin",
        revisedAt:        new Date().toISOString(),
      };

      // 1. Update primary employee record — save fixed breakdown + manual variable components
      await updateDoc(doc(db, "employee_records_complete", empRecordId), {
        salaryBreakdown:    newBreakdown,
        variableComponents: { ...newVariableComponents, ...retentionTranches },
        annualCTC:          String(newTotalCTC),
        grossMonthlySalary: String(newGross),
        appraisalLog:       arrayUnion(appraisalEntry),
        updatedAt:          serverTimestamp(),
      });

      // 2. Sync users collection (+ optionally performance_kpi)
      const userUid =
        employee.uid ||
        (employee.id !== employee.employeeId ? employee.id : null);
      if (userUid) {
        try {
          const userUpdate = {
            grossMonthlySalary: String(newGross),
            annualCTC:          String(newTotalCTC),
          };
          if (setKpi) userUpdate.performance_kpi = kpiFloat;
          await updateDoc(doc(db, "users", userUid), userUpdate);
        } catch (syncErr) {
          console.warn("Could not sync to users collection:", syncErr);
        }
      }

      toast.success(`Salary breakdown saved! Total CTC: ₹${fmtINR(newTotalCTC)}`);
      if (onAppraisalDone) onAppraisalDone(appraisalEntry);
      onClose();
    } catch (e) {
      console.error("Appraisal save error:", e);
      toast.error("Failed to save appraisal: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div
      className={styles.backdrop}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={styles.modal}>
        {/* Header */}
        <button className={styles.closeBtn} onClick={onClose} title="Close">
          <FaTimes />
        </button>
        <div className={styles.header}>
          <span className={styles.headerIcon}>
            {activeTab === "salary" ? <FaTrophy /> : <FaUserTie />}
          </span>
          <div>
            <h2 className={styles.title}>
              {activeTab === "salary" ? "Salary Appraisal" : "Role Change / Promotion"}
            </h2>
            <p className={styles.subtitle}>
              {employee?.name}
              {employee?.employeeId && (
                <span className={styles.empId}> · {employee.employeeId}</span>
              )}
            </p>
          </div>
        </div>

        {/* ── Tab switcher ── */}
        <div className={styles.tabSwitcher}>
          <button
            type="button"
            className={`${styles.tabBtn} ${activeTab === "salary" ? styles.tabBtnActive : ""}`}
            onClick={() => setActiveTab("salary")}
          >
            💰 Salary Appraisal
          </button>
          <button
            type="button"
            className={`${styles.tabBtn} ${activeTab === "promote" ? styles.tabBtnActivePromote : ""}`}
            onClick={() => setActiveTab("promote")}
          >
            👤 Role Change
          </button>
        </div>

        {/* ── Promote tab ── */}
        {activeTab === "promote" && (
          <div>
            <div className={styles.roleBar}>
              <div className={styles.roleChip}>
                <span className={styles.chipLabel}>Current Role</span>
                <span className={styles.roleChipValue}>{employee?.role || "employee"}</span>
              </div>
              <FaArrowRight className={styles.ctcArrow} />
              <div className={`${styles.roleChip} ${styles.roleChipNew}`}>
                <span className={styles.chipLabel}>New Role</span>
                <span className={styles.roleChipValue}>{promoteRole}</span>
              </div>
            </div>

            <div className={styles.form}>
              <div className={styles.formGroup}>
                <label className={styles.label}>New Role <span className={styles.required}>*</span></label>
                <select className={styles.input} value={promoteRole} onChange={e => setPromoteRole(e.target.value)}>
                  <option value="employee">Employee</option>
                  <option value="admin">Admin</option>
                  {userData?.role === "superadmin" && <option value="superadmin">Super Admin</option>}
                </select>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>New Position <span className={styles.optionalTag}>(optional)</span></label>
                  <input
                    className={styles.input}
                    type="text"
                    placeholder={employee?.position || "e.g. Senior Faculty"}
                    value={promotePosition}
                    onChange={e => setPromotePosition(e.target.value)}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>New Department <span className={styles.optionalTag}>(optional)</span></label>
                  <input
                    className={styles.input}
                    type="text"
                    placeholder={employee?.department || "e.g. Academic"}
                    value={promoteDept}
                    onChange={e => setPromoteDept(e.target.value)}
                  />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Effective Date <span className={styles.required}>*</span></label>
                <input
                  className={styles.input}
                  type="date"
                  value={promoteDate}
                  onChange={e => setPromoteDate(e.target.value)}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Reason / Remarks</label>
                <textarea
                  className={styles.textarea}
                  placeholder="e.g. Promoted based on exceptional performance..."
                  value={promoteReason}
                  onChange={e => setPromoteReason(e.target.value)}
                  rows={3}
                />
              </div>
            </div>

            <div className={styles.actions}>
              <button className={styles.cancelBtn} onClick={onClose} disabled={promoteSaving}>
                Cancel
              </button>
              <button
                className={styles.promoteSaveBtn}
                onClick={handlePromote}
                disabled={promoteSaving || !promoteDate || promoteRole === (employee?.role || "employee")}
              >
                {promoteSaving ? "Saving…" : "👤 Confirm Role Change"}
              </button>
            </div>
          </div>
        )}

        {/* ── Salary Appraisal tab ── */}
        {activeTab === "salary" && <>
        {/* ── CTC comparison bar ── */}
        <div className={styles.ctcBar}>
          <div className={styles.ctcChip}>
            <span className={styles.chipLabel}>Current Total CTC</span>
            <span className={styles.chipValue}>
              {currentTotalCTC
                ? `₹${fmtINR(currentTotalCTC)}`
                : <em className={styles.notSet}>Not set</em>}
            </span>
          </div>
          <FaArrowRight className={styles.ctcArrow} />
          <div className={`${styles.ctcChip} ${styles.ctcChipNew}`}>
            <span className={styles.chipLabel}>New Total CTC</span>
            <span className={styles.chipValue}>
              {hasChanges && newTotalCTC > 0
                ? `₹${fmtINR(newTotalCTC)}`
                : <em className={styles.notSet}>Enter percentages to preview</em>}
            </span>
          </div>
          {ctcHikePct !== 0 && newTotalCTC > 0 && (
            <span
              className={`${styles.incrBadge} ${
                ctcHikePct >= 0 ? styles.positive : styles.negative
              }`}
            >
              {ctcHikePct >= 0 ? <FaArrowUp /> : <FaArrowDown />}
              &nbsp;{Math.abs(ctcHikePct)}% CTC
            </span>
          )}
        </div>

        {/* ── Monthly gross bar (secondary) ── */}
        <div
          className={styles.ctcBar}
          style={{ marginTop: "-8px", background: "#F9FAFB", border: "1px solid #E2E8F0" }}
        >
          <div className={styles.ctcChip}>
            <span className={styles.chipLabel}>Current Monthly Gross</span>
            <span className={styles.chipValue} style={{ fontSize: "0.93rem" }}>
              {currentMonthlyGross ? `₹${fmtINR(currentMonthlyGross)}` : "—"}
            </span>
          </div>
          <FaArrowRight className={styles.ctcArrow} />
          <div className={`${styles.ctcChip} ${styles.ctcChipNew}`}>
            <span className={styles.chipLabel}>New Monthly Gross</span>
            <span className={styles.chipValue} style={{ fontSize: "0.93rem" }}>
              {hasChanges && newMonthlyGross ? `₹${fmtINR(newMonthlyGross)}` : "—"}
            </span>
          </div>
        </div>

        {/* Single stacked preview card: Old breakdown integrated into the same box as New preview */}
        <div className={styles.previewCard} style={{ marginTop: 12 }}>
          <div className={styles.previewHeader}>Salary Breakdown</div>

          {/* Old (current) breakdown shown first inside the same card, matching profile layout */}
          <div style={{ padding: 12 }}>
            <h4 style={{ color: '#1F2937', fontSize: 13, letterSpacing: '0.6px' }}>MONTHLY GROSS COMPONENTS</h4>
            <div className={styles.previewGrid}>
              <div className={styles.previewItem}><span>BASIC</span><strong>₹{fmtINR(Number(displayCurrentBreakdown?.basic || 0))}</strong></div>
              <div className={styles.previewItem}><span>HRA</span><strong>₹{fmtINR(Number(displayCurrentBreakdown?.hra || 0))}</strong></div>
              <div className={styles.previewItem}><span>LTA</span><strong>₹{fmtINR(Number(displayCurrentBreakdown?.lta || 0))}</strong></div>
              <div className={styles.previewItem}><span>MEDICAL ALLOWANCE</span><strong>₹{fmtINR(Number(displayCurrentBreakdown?.medical || 0))}</strong></div>
              <div className={styles.previewItem}><span>TELEPHONE & INTERNET</span><strong>₹{fmtINR(Number(displayCurrentBreakdown?.telephone || 0))}</strong></div>
              <div className={styles.previewItem}><span>SPECIAL ALLOWANCE</span><strong>₹{fmtINR(Number(displayCurrentBreakdown?.special || 0))}</strong></div>
              <div className={`${styles.previewItem} ${styles.highlight}`}><span>MONTHLY GROSS</span><strong>₹{fmtINR(currentMonthlyGross)}</strong></div>
              <div className={styles.previewItem}><span>ANNUAL GROSS</span><strong>₹{fmtINR(currentAnnualGross)}</strong></div>
            </div>

            <h4 style={{ color: '#1F2937', fontSize: 13, letterSpacing: '0.6px', marginTop: 12 }}>VARIABLE & ONE-TIME COMPONENTS</h4>
            <div className={styles.previewGrid} style={{ marginTop: 8 }}>
              <div className={styles.previewItem}><span>JOINING BONUS</span><strong>₹{fmtINR(Number(currentVariable.joiningBonus || 0))}</strong></div>
              <div className={styles.previewItem}><span>VARIABLE PAY POOL (ANNUAL MAX)</span><strong>₹{fmtINR(Number(currentVariable.variablePayPool || 0))}</strong></div>
              <div className={styles.previewItem}><span>RETENTION BONUS POOL</span><strong>₹{fmtINR(Number(currentVariable.retentionPool || 0))}</strong></div>
            </div>

            <div style={{ marginTop: 12 }} className={styles.ctcBar}>
              <div style={{ padding: 12 }}>
                <strong style={{ color: '#4B5563' }}>TOTAL CTC</strong>
              </div>
              <div style={{ padding: 12 }}>
                <strong style={{ fontSize: 18 }}>₹{fmtINR(currentTotalCTC)}</strong>
              </div>
            </div>
          </div>

          {/* Percentage input: Gross % and (superadmin) Variable % */}
          <div style={{ padding: '12px 16px', borderTop: '1px solid #EEF2FF', borderBottom: '1px solid #EEF2FF', marginTop: 8, background: '#fff' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'flex-start' }}>
              <label style={{ fontSize: 13, marginRight: 8 }}>Gross %</label>
              <div className={styles.pctInputWrapper} style={{ margin: 0 }}>
                <input
                  className={`${styles.input} ${styles.pctInput}`}
                  type="number"
                  step="0.5"
                  placeholder="e.g. 10"
                  value={hikePctInput}
                  onChange={(e) => setHikePctInput(e.target.value)}
                  style={{ width: 140 }}
                />
                <span className={styles.pctSymbol}>%</span>
              </div>
              {/* variable percent removed — use manual Variable Pay Pool field below */}
            </div>
          </div>

          {/* New breakdown — show in same grouped layout when changes present */}
          {!hasChanges ? (
            <div style={{ padding: 20, color: '#6B7280' }}>
              Enter Gross % above to preview the new salary breakdown here.
            </div>
          ) : (
            <div style={{ padding: 12 }}>
              <h4 style={{ color: '#1F2937', fontSize: 13, letterSpacing: '0.6px' }}>MONTHLY GROSS COMPONENTS</h4>
              <div className={styles.previewGrid}>
                <div className={styles.previewItem}><span>BASIC</span><strong>₹{fmtINR(newBreakdown.basic)}</strong></div>
                <div className={styles.previewItem}><span>HRA</span><strong>₹{fmtINR(newBreakdown.hra)}</strong></div>
                <div className={styles.previewItem}><span>LTA</span><strong>₹{fmtINR(newBreakdown.lta)}</strong></div>
                <div className={styles.previewItem}><span>MEDICAL ALLOWANCE</span><strong>₹{fmtINR(newBreakdown.medical)}</strong></div>
                <div className={styles.previewItem}><span>TELEPHONE & INTERNET</span><strong>₹{fmtINR(newBreakdown.telephone)}</strong></div>
                <div className={styles.previewItem}><span>SPECIAL ALLOWANCE</span><strong>₹{fmtINR(newBreakdown.special)}</strong></div>
                <div className={`${styles.previewItem} ${styles.highlight}`}><span>MONTHLY GROSS</span><strong>₹{fmtINR(newMonthlyGross)}</strong></div>
                <div className={styles.previewItem}><span>ANNUAL GROSS</span><strong>₹{fmtINR(newAnnualGross)}</strong></div>
              </div>

              <h4 style={{ color: '#1F2937', fontSize: 13, letterSpacing: '0.6px', marginTop: 12 }}>VARIABLE & ONE-TIME COMPONENTS</h4>
              <div className={styles.previewGrid} style={{ marginTop: 8 }}>
                <div className={styles.previewItem}><span>JOINING BONUS</span><strong>₹{fmtINR(newVariableComponents.joiningBonus)}</strong></div>
                <div className={styles.previewItem}><span>VARIABLE PAY POOL (ANNUAL MAX)</span><strong>₹{fmtINR(newVariableComponents.variablePayPool)}</strong></div>
                {/* Retention shown as read-only (unchanged by appraisal) */}
                <div className={styles.previewItem}><span>RETENTION BONUS POOL</span><strong>₹{fmtINR(newVariableComponents.retentionPool)}</strong></div>
              </div>

              <div style={{ marginTop: 12 }} className={styles.ctcBar}>
                <div style={{ padding: 12 }}>
                  <strong style={{ color: '#4B5563' }}>TOTAL CTC</strong>
                </div>
                <div style={{ padding: 12 }}>
                  <strong style={{ fontSize: 18 }}>₹{fmtINR(newTotalCTC)}</strong>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Part 1: Gross Salary — fixed; ratios maintained on appraisal ── */}
        <div className={styles.form}>
          {hasFixedBreakdown ? (
            <div style={{ padding: 8, color: '#6B7280' }}>Fixed gross components are shown in the preview above.</div>
          ) : (
            <>
              <div style={{ padding: 12, background: '#F8FAFC', borderRadius: 6, border: '1px solid #E6EEF6' }}>
                <strong>Simple Appraisal UI</strong>
                <p style={{ margin: '6px 0 0', color: '#6B7280' }}>
                  Enter Gross % (applies proportionally to Basic/HRA/LTA/Medical/Telephone/Special).
                  The preview below will show the computed new breakdown.
                </p>
              </div>
            </>
          )}

          {/* ── Part 2: Variable & one-time components — superadmin-only edit ── */}
            <div className={styles.formGroup}>
              <label className={styles.label} style={{ marginTop: 6 }}>
                Variable &amp; One-time Components {!isSuperAdmin && <span className={styles.optionalTag}>(Superadmin only)</span>}
              </label>
              {!isSuperAdmin && (
                <p className={styles.pctWarn}>Only Superadmin can edit these fields. Values shown are read-only.</p>
              )}
              {isSuperAdmin && (
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" checked={includeVarRet} onChange={e => setIncludeVarRet(e.target.checked)} />
                    <span style={{ fontSize: 13 }}>Include variable pay pool in this appraisal</span>
                  </label>
                  <span style={{ color: '#6B7280', fontSize: 13 }}>When unchecked, existing variable pool remains unchanged.</span>
                </div>
              )}
            </div>
            <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Variable Pay Pool (Annual Max, KPI-based)</label>
              <input className={styles.input} type="number" value={variablePayPoolInput} disabled={!isSuperAdmin} onChange={(e) => setVariablePayPoolInput(e.target.value)} />
              <p className={styles.pctHint} style={{ margin: 0 }}>Max committed pool — actual payout = Pool × Performance KPI%.</p>
            </div>
          </div>
          {/* Retention is not editable in appraisal (kept read-only in preview above) */}

          <div className={styles.formGroup}>
            <label className={styles.label}>
              Effective Date <span className={styles.required}>*</span>
            </label>
            <input
              className={styles.input}
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Reason / Remarks</label>
            <textarea
              className={styles.textarea}
              placeholder="e.g. Annual performance appraisal — exceptional contribution, promoted to Senior…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        {/* ── KPI section (variable-pay employees only) ── */}
        {showKpiSection && (
          <div className={styles.kpiSection}>
            <div className={styles.kpiSectionHeader}>
              <label className={styles.kpiToggleLabel}>
                <input
                  type="checkbox"
                  checked={setKpi}
                  onChange={(e) => setSetKpi(e.target.checked)}
                  style={{ marginRight: 7 }}
                />
                Set Performance KPI % at this appraisal
              </label>
              <span className={styles.kpiOptional}>(optional)</span>
            </div>

            {setKpi && (
              <>
                <div className={styles.kpiRow}>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={5}
                    value={kpiPct}
                    onChange={(e) => setKpiPctInput(e.target.value)}
                    className={styles.kpiSlider}
                  />
                  <div className={styles.pctInputWrapper} style={{ width: 90 }}>
                    <input
                      className={`${styles.input} ${styles.pctInput}`}
                      type="number"
                      min={0}
                      max={100}
                      step={1}
                      value={kpiPctInput}
                      onChange={(e) => setKpiPctInput(e.target.value)}
                      style={{ height: 36 }}
                    />
                    <span className={styles.pctSymbol}>%</span>
                  </div>
                </div>

                {newVariableComponents.variablePayPool > 0 && (
                  <div className={styles.variableReleasePanel}>
                    <span>Max Variable Pool:</span>
                    <strong>₹{fmtINR(newVariableComponents.variablePayPool)}</strong>
                    <span style={{ color: "#6B7280" }}>×</span>
                    <strong>{kpiPct}%</strong>
                    <span>=</span>
                    <span className={styles.releaseAmt}>
                      ₹{fmtINR(estimatedVariableRelease)}
                    </span>
                    <span style={{ color: "#15803D", fontSize: "0.78rem" }}>
                      released this FY
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Removed duplicate separate New Salary Breakdown card — new values shown in stacked preview above */}

        {/* ── Actions ── */}
        <div className={styles.actions}>
          <button className={styles.cancelBtn} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className={styles.saveBtn}
            onClick={handleSave}
            disabled={saving || !canSave}
          >
            {saving ? "Saving…" : "✅ Save Salary Breakdown"}
          </button>
        </div>
        {/* end salary tab */}
        </>
        }
      </div>
    </div>
  );
};

export default AppraisalModal;
